// LiveAthleteView.tsx

import React from 'react';
import styles from './LiveAthleteView.module.scss';
import classNames from 'classnames';
import { LiveRepFrame } from '../../types/automatic';
import { VelocityRate } from '../../constants';
import { useSessionData } from '../../providers';
import RepInstance from './RepInstance';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import { AutomaticMetricsType } from '../../constants';

// Use real gym members from session data

function classifyValue(
  value: number | undefined,
  min: number | undefined,
  max: number | undefined,
) {
  if (value == null || min == null || max == null) return VelocityRate.Good;
  if (value < min) return VelocityRate.Fast;
  if (value > max) return VelocityRate.Slow;
  return VelocityRate.Good;
}

interface LiveAthleteViewProps {
  liveSets: SetInfo[];
  finishedSets: SetInfo[];
  selectedGroupId?: string;
  setSelectedGroupId?: React.Dispatch<React.SetStateAction<string>>;
  selectedAthleteId: string;
  setSelectedAthleteId: React.Dispatch<React.SetStateAction<string>>;
  selectedRackNumber: string;
  setSelectedRackNumber: React.Dispatch<React.SetStateAction<string>>;
}

interface SetInfo {
  userId: string;
  metrics: LiveRepFrame[];
  rackNumber: number;
  performedSets: number;
  setId: string;
  concentricType: AutomaticMetricsType;
  eccentricType: AutomaticMetricsType;
  finishedAt?: number;
  source?: 'live' | 'historical' | 'liveFinished';
  performedReps?: number;
}

/**
 * Determine if a set is completed or not and its color.
 * Returns {statusLabel, statusColor}.
 */
function getSetStatus(
  isActive: boolean,
  performedCount: number,
  totalRepsNeeded: number,
  fastCount: number,
  slowCount: number,
  goodCount: number,
): {
  statusLabel: string;
  statusColor: 'orange' | 'white' | 'green' | 'yellow' | 'red';
} {
  let statusLabel = 'In Progress';
  let statusColor: 'orange' | 'white' | 'green' | 'yellow' | 'red' = 'orange';

  const totalAttempts = goodCount + fastCount + slowCount;

  if (isActive) {
    // Live sets are always shown as In Progress until session ends
    statusLabel = 'In Progress';
    statusColor = 'orange';
  } else {
    // Finished lists (liveFinished or historical)
    if (totalRepsNeeded != null && performedCount < totalRepsNeeded) {
      statusLabel = 'Not Completed';
      statusColor = 'white';
    } else {
      // Completed scenario (not active means someone else took rack)
      if (totalAttempts > 0) {
        const g = goodCount / totalAttempts;
        const f = fastCount / totalAttempts;
        const s = slowCount / totalAttempts;
        if (g > f && g > s) {
          statusLabel = 'Completed';
          statusColor = 'green';
        } else if (f > g && f > s) {
          statusLabel = 'Completed';
          statusColor = 'yellow';
        } else if (s > g && s > f) {
          statusLabel = 'Completed';
          statusColor = 'red';
        } else {
          statusLabel = 'Completed';
          statusColor = 'green';
        }
      } else {
        statusLabel = 'Completed';
        statusColor = 'green';
      }
    }
  }

  return { statusLabel, statusColor };
}

const LiveAthleteView: React.FC<LiveAthleteViewProps> = ({
  liveSets,
  finishedSets,
  selectedAthleteId,
  setSelectedAthleteId,
  selectedRackNumber,
  setSelectedRackNumber,
  selectedGroupId,
  setSelectedGroupId,
}) => {
  const { groups, members, mergedExercisesMap } = useSessionData();

  // --- Dropdown options ---
  // Get all unique athletes from liveSets and finishedSets
  const allSets = React.useMemo(
    () => [...liveSets, ...finishedSets],
    [liveSets, finishedSets],
  );
  const athleteOptions = React.useMemo(() => {
    const ids = new Set<string>();
    const athletes: { id: string; firstName: string; lastName: string }[] = [];
    allSets.forEach((set) => {
      if (!ids.has(set.userId)) {
        const member = members[set.userId];
        if (member) {
          athletes.push({
            id: member.id,
            firstName: member.firstName ?? member.name?.split(' ')?.[0] ?? '',
            lastName:
              member.lastName ??
              (member.name && member.name.split(' ').length > 1
                ? member.name.split(' ')[1]
                : ''),
          });
          ids.add(set.userId);
        }
      }
    });
    // Sort by first name A-Z
    return athletes.sort((a, b) => a.firstName.localeCompare(b.firstName));
  }, [allSets, members]);

  // Get all unique rack numbers from liveSets and finishedSets
  const rackOptions = React.useMemo(() => {
    const racks = Array.from(new Set(allSets.map((set) => set.rackNumber)));
    return racks.sort((a, b) => a - b);
  }, [allSets]);

  // --- Filtering logic ---
  const filterSets = (sets: SetInfo[]) => {
    const groupMemberSet =
      (selectedGroupId &&
        selectedGroupId !== 'all' &&
        new Set(groups[selectedGroupId]?.memberIds || [])) ||
      null;
    return sets.filter((set) => {
      const athleteMatch =
        selectedAthleteId === 'all' || set.userId === selectedAthleteId;
      const groupMatch =
        !groupMemberSet || (groupMemberSet && groupMemberSet.has(set.userId));
      const rackMatch =
        selectedRackNumber === 'all' ||
        set.rackNumber === Number(selectedRackNumber);
      return athleteMatch && groupMatch && rackMatch;
    });
  };

  // --- Dropdown UI ---
  const renderFilters = () => (
    <div className={styles.filtersRow}>
      <FormControl
        size="small"
        className={classNames(styles.formControl, styles.filterControl)}
      >
        <InputLabel className={styles.inputLabel} id="group-filter-label">
          Group
        </InputLabel>
        <Select
          labelId="group-filter-label"
          value={selectedGroupId ?? 'all'}
          label="Group"
          onChange={(e) =>
            setSelectedGroupId && setSelectedGroupId(e.target.value as string)
          }
          className={styles.select}
        >
          <MenuItem value="all">All Groups</MenuItem>
          {Object.values(groups).map((g) => (
            <MenuItem key={g.id} value={g.id}>
              {g.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl
        size="small"
        className={classNames(styles.formControl, styles.filterControl)}
      >
        <InputLabel className={styles.inputLabel} id="rack-filter-label">
          Rack
        </InputLabel>
        <Select
          labelId="rack-filter-label"
          value={selectedRackNumber}
          label="Rack"
          onChange={(e) => setSelectedRackNumber(e.target.value)}
          className={styles.select}
        >
          <MenuItem value="all">All Racks</MenuItem>
          {rackOptions.map((rack) => (
            <MenuItem key={rack} value={rack}>
              {rack}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl
        size="small"
        className={classNames(styles.formControl, styles.filterControl)}
      >
        <InputLabel className={styles.inputLabel} id="athlete-filter-label">
          Athlete
        </InputLabel>
        <Select
          labelId="athlete-filter-label"
          value={selectedAthleteId}
          label="Athlete"
          onChange={(e) => setSelectedAthleteId(e.target.value)}
          className={styles.select}
        >
          <MenuItem value="all">All Athletes</MenuItem>
          {athleteOptions.map((athlete) => (
            <MenuItem key={athlete.id} value={athlete.id}>
              {athlete.firstName} {athlete.lastName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );

  // --- Click-to-filter on athlete name ---
  const handleAthleteNameClick = (userId: string) => {
    setSelectedAthleteId(userId);
  };

  const renderSetRow = (setInfo: SetInfo, isActive: boolean) => {
    const { userId, metrics, setId } = setInfo;
    const member = members[userId];
    const memberName = member
      ? `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim()
      : `User ${userId}`;
    const lastFrame = metrics[metrics.length - 1];
    const rackNumber = lastFrame.rackNumber ?? 1;
    const preset = lastFrame.preset || null;
    const totalRepsNeeded = preset?.reps ?? 10;
    const performedCount = metrics.length;

    let fastCount = 0;
    let slowCount = 0;
    let goodCount = 0;
    let loggedWeight = 0;

    metrics.forEach((frame) => {
      const {
        minConcentric,
        maxConcentric,
        minEccentric,
        maxEccentric,
        weight,
        concentricType,
        eccentricType,
      } = frame.preset || ({} as any);
      if (weight != null) loggedWeight = weight;

      let cClass: VelocityRate = VelocityRate.Good;
      let eClass: VelocityRate = VelocityRate.Good;
      if (concentricType === AutomaticMetricsType.Velocity) {
        // Velocity: below min => slow, above max => fast
        if (
          frame.upDuration != null &&
          minConcentric != null &&
          maxConcentric != null
        ) {
          if (frame.upDuration < minConcentric) cClass = VelocityRate.Slow;
          else if (frame.upDuration > maxConcentric) cClass = VelocityRate.Fast;
          else cClass = VelocityRate.Good;
        }
      } else {
        // Tempo default
        cClass = classifyValue(frame.upDuration, minConcentric, maxConcentric);
      }

      if (eccentricType === AutomaticMetricsType.Velocity) {
        if (
          frame.downDuration != null &&
          minEccentric != null &&
          maxEccentric != null
        ) {
          if (frame.downDuration < minEccentric) eClass = VelocityRate.Slow;
          else if (frame.downDuration > maxEccentric)
            eClass = VelocityRate.Fast;
          else eClass = VelocityRate.Good;
        }
      } else {
        eClass = classifyValue(frame.downDuration, minEccentric, maxEccentric);
      }

      [cClass, eClass].forEach((cls) => {
        if (cls === VelocityRate.Fast) fastCount++;
        else if (cls === VelocityRate.Slow) slowCount++;
        else goodCount++;
      });
    });

    const { statusLabel, statusColor } = getSetStatus(
      isActive,
      performedCount,
      totalRepsNeeded,
      fastCount,
      slowCount,
      goodCount,
    );

    let exerciseName = 'Unknown Exercise';
    if (metrics.length > 0 && metrics[0].exerciseId) {
      const exercise = mergedExercisesMap[metrics[0].exerciseId];
      if (exercise) {
        exerciseName = exercise.name;
      }
    }

    // Always use 10 columns for the grid
    const gridColumns = 10;
    const repsToShow = Math.max(gridColumns, performedCount, totalRepsNeeded);
    const repItems = [];
    for (let i = 0; i < repsToShow; i++) {
      if (i < performedCount) {
        const frame = metrics[i];
        let eClass = VelocityRate.Good,
          cClass = VelocityRate.Good;
        const showData = !!frame;
        if (showData) {
          const {
            minConcentric,
            maxConcentric,
            minEccentric,
            maxEccentric,
            concentricType,
            eccentricType,
          } = frame.preset || ({} as any);
          if (concentricType === AutomaticMetricsType.Velocity) {
            if (
              frame.upDuration != null &&
              minConcentric != null &&
              maxConcentric != null
            ) {
              if (frame.upDuration < minConcentric) cClass = VelocityRate.Slow;
              else if (frame.upDuration > maxConcentric)
                cClass = VelocityRate.Fast;
              else cClass = VelocityRate.Good;
            }
          } else {
            cClass = classifyValue(
              frame.upDuration,
              minConcentric,
              maxConcentric,
            );
          }

          if (eccentricType === AutomaticMetricsType.Velocity) {
            if (
              frame.downDuration != null &&
              minEccentric != null &&
              maxEccentric != null
            ) {
              if (frame.downDuration < minEccentric) eClass = VelocityRate.Slow;
              else if (frame.downDuration > maxEccentric)
                eClass = VelocityRate.Fast;
              else eClass = VelocityRate.Good;
            }
          } else {
            eClass = classifyValue(
              frame.downDuration,
              minEccentric,
              maxEccentric,
            );
          }
        }
        repItems.push(
          <RepInstance
            key={i}
            repNumber={i + 1}
            eccentricClass={showData ? eClass : VelocityRate.Empty}
            concentricClass={showData ? cClass : VelocityRate.Empty}
          />,
        );
      } else if (!preset?.isAmrap && i < totalRepsNeeded) {
        // Render empty RepInstance for unperformed reps
        repItems.push(
          <RepInstance
            key={i}
            repNumber={i + 1}
            eccentricClass={VelocityRate.Empty}
            concentricClass={VelocityRate.Empty}
          />,
        );
      }
      // If i >= totalRepsNeeded, do not render extra RepInstances
    }

    return (
      <div
        className={classNames(styles.userRow, {
          [styles.nonLive]: !isActive,
        })}
        key={setId}
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerTop}>
            <span
              className={styles.userName}
              style={{ cursor: 'pointer' }}
              onClick={() => handleAthleteNameClick(userId)}
              title="Filter by this athlete"
            >
              {memberName}
            </span>
            <span className={styles.separatorDot}>•</span>
            <span className={styles.rackNumber}>{`Rack: ${rackNumber}`}</span>
          </div>
          <div className={styles.headerBottom}>
            {fastCount > 0 && (
              <span
                className={classNames(styles.attempts, {
                  [styles.fast]: fastCount > 0,
                })}
              >
                <WarningAmberOutlinedIcon
                  className={styles.warningIcon}
                  style={{ color: 'var(--color-warning)', fontSize: '24px' }}
                />
                {` ${fastCount} Reps`}
              </span>
            )}
            {slowCount > 0 && (
              <span
                className={classNames(styles.attempts, {
                  [styles.slow]: slowCount > 0,
                })}
              >
                <WarningAmberOutlinedIcon
                  className={styles.warningIcon}
                  style={{ color: 'var(--color-danger)', fontSize: '24px' }}
                />
                {` ${slowCount} Reps`}
              </span>
            )}
          </div>
          <div
            className={classNames(styles.statusLabel, {
              [styles.orangeBorder]: statusColor === 'orange',
              [styles.whiteBorder]: statusColor === 'white',
              [styles.greenBorder]: statusColor === 'green',
              [styles.yellowBorder]: statusColor === 'yellow',
              [styles.redBorder]: statusColor === 'red',
            })}
          >
            {statusLabel === 'Completed' && (
              <CheckIcon
                style={{
                  fontSize: '18px',
                  marginRight: '4px',
                  color: 'var(--color-text-1)',
                }}
              />
            )}
            {statusLabel}
          </div>
        </div>
        {/* Main Content */}
        <div className={styles.mainContent}>
          <div className={styles.leftSide}>
            <div className={styles.exerciseName}>{exerciseName}</div>
            <div className={styles.loggedWeight}>Logged Weight:</div>
            <div className={styles.loggedWeightValue}>{loggedWeight} lbs</div>
          </div>
          <div className={styles.rightSide}>
            <div className={styles.repList}>{repItems}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {renderFilters()}
      <div className={styles.container}>
        {/* Three tiers: live (top), then liveFinished, then historical (bottom) */}
        {filterSets(liveSets).map((setInfo) => renderSetRow(setInfo, true))}
        {filterSets(
          finishedSets
            .filter((s) => s.source === 'liveFinished')
            .filter(
              (finished) =>
                !liveSets.some((live) => live.setId === finished.setId),
            )
            .sort((a, b) => (b.finishedAt || 0) - (a.finishedAt || 0)),
        ).map((setInfo) => renderSetRow(setInfo, false))}
        {filterSets(
          finishedSets
            .filter((s) => s.source === 'historical')
            .filter(
              (finished) =>
                !liveSets.some((live) => live.setId === finished.setId),
            )
            .sort((a, b) => (b.finishedAt || 0) - (a.finishedAt || 0)),
        ).map((setInfo) => renderSetRow(setInfo, false))}
      </div>
    </div>
  );
};

export default LiveAthleteView;
